import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import jsQR from 'jsqr'
import { api } from '../api/client'
import type { DeviceDetail, Equipment } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Card, ErrorBox, Loading } from '../components/ui'
import { isDeviceCode, normalizeDeviceCode } from '../lib/deviceCode'
import { NewProjectForm } from './ProjectsPage'
import { CaseBoxChoice, caseBoxFromScan } from '../components/CaseBoxCard'

/**
 * Scanning af QR-mærkatet på en enhed.
 *
 * Mærkatet indeholder en URL, så mobilens eget kamera også virker - denne side er
 * genvejen, når appen allerede er åben. Koden kan desuden tastes ind i hånden, for
 * et mærkat i et teknikrum bliver snavset, og så nytter kameraet ikke noget
 * (docs/DECISIONS.md beslutning 024).
 */
export function ScanPage() {
  const navigate = useNavigate()
  const { isProjectManager } = useAuth()
  // Ny servicesag direkte fra Service - samme formular som under Sager, låst til service.
  const [showNewService, setShowNewService] = useState(false)
  // Sagen blev oprettet, men enhederne kom ikke på. Linket fører ind på sagen.
  const [createdWarning, setCreatedWarning] = useState<{ id: number; message: string } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [scanning, setScanning] = useState(false)
  const [manualCode, setManualCode] = useState('')
  // Sagskassen er scannet (beslutning 144): valget bil/værksted vises her, uden at forlade scanneren.
  const [scannedBox, setScannedBox] = useState<{ projectId: number; number: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Kameraet skal slukkes, når man forlader siden - ellers bliver lampen ved med at lyse.
  useEffect(() => {
    return () => stopCamera(videoRef.current)
  }, [])

  async function lookup(scanned: string) {
    // Sagskassens mærkat (beslutning 143) peger på /s/<sagens id> - ingen kode at slå op.
    // Valget "i min bil" / "på værkstedet" vises lige her (beslutning 144).
    const caseBox = caseBoxFromScan(scanned)
    if (caseBox) {
      stopCamera(videoRef.current)
      setError(null)
      setScannedBox(caseBox)
      return
    }

    // Mærkatet indeholder en URL - koden pilles ud her, da en hel URL ikke kan
    // sendes som et led i API-stien.
    const code = normalizeDeviceCode(scanned)

    if (!isDeviceCode(code)) {
      setError(
        `"${scanned.slice(0, 40)}" er ikke en kode herfra. ` +
          'Scan QR-koden på mærkatet, eller tast de 8 tegn under den.'
      )
      return
    }

    setBusy(true)
    setError(null)
    try {
      const found = await api.get<DeviceDetail>(`/api/devices/by-code/${code}`)
      stopCamera(videoRef.current)
      navigate(`/devices/${found.device.id}`)
      return
    } catch {
      // Ikke en enhed. Koderne ser ens ud paa tvaers af enheder, varer og
      // vaerktoej (ShortCode), saa der er ikke andet at goere end at spoerge
      // videre - man ved foerst, hvad man har scannet, naar noget svarer.
    }

    try {
      // Vaerktoej (beslutning 096). Maerkatet peger paa /m/<kode>, men den, der
      // staar med scanneren aaben, skal ikke behoeve at vide det.
      const tool = await api.get<Equipment>(`/api/equipment/by-code/${code}`)
      stopCamera(videoRef.current)
      navigate(`/equipment/${tool.id}`)
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Koden hører hverken til en enhed eller et stykke værktøj.'
      )
    } finally {
      setBusy(false)
    }
  }

  async function startCamera() {
    setError(null)

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Denne browser kan ikke bruge kameraet. Tast koden fra mærkatet i stedet.')
      return
    }

    try {
      // Bagkameraet - mærkatet sidder på enheden, ikke på brugeren.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }
      })

      const video = videoRef.current
      if (!video) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      video.srcObject = stream
      await video.play()
      setScanning(true)
      void readFrames()
    } catch {
      setError(
        'Der er ikke adgang til kameraet. Giv appen lov i browserens indstillinger, ' +
          'eller tast koden fra mærkatet.'
      )
    }
  }

  /**
   * Læser billeder fra kameraet, indtil der findes en QR-kode.
   *
   * BarcodeDetector er indbygget i Chrome/Android og er både hurtigere og mere
   * strømbesparende; jsQR er reserven, som bl.a. dækker iPhone.
   */
  async function readFrames() {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (!context) return

    const detector =
      'BarcodeDetector' in window
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          new (window as any).BarcodeDetector({ formats: ['qr_code'] })
        : null

    let running = true
    const stopWhenGone = () => {
      running = Boolean(videoRef.current?.srcObject)
    }

    while (running) {
      stopWhenGone()
      if (!running) break

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        context.drawImage(video, 0, 0, canvas.width, canvas.height)

        let text: string | null = null

        if (detector) {
          try {
            const codes = await detector.detect(canvas)
            text = codes[0]?.rawValue ?? null
          } catch {
            // Falder igennem til jsQR nedenfor.
          }
        }

        if (!text) {
          const image = context.getImageData(0, 0, canvas.width, canvas.height)
          text = jsQR(image.data, image.width, image.height)?.data ?? null
        }

        if (text) {
          const code = normalizeDeviceCode(text)

          // En fremmed QR-kode i billedet skal ikke stoppe scanningen - bliv ved
          // at kigge, indtil der dukker et rigtigt mærkat op: en enhed, et stykke
          // værktøj eller en sagskasse.
          if (isDeviceCode(code) || caseBoxFromScan(text)) {
            setScanning(false)
            await lookup(text)
            return
          }
        }
      }

      // ~10 billeder i sekundet er rigeligt og sparer på batteriet.
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
  }

  function stop() {
    stopCamera(videoRef.current)
    setScanning(false)
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isProjectManager && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--primary" onClick={() => setShowNewService((v) => !v)}>
            {showNewService ? 'Luk' : 'Ny servicesag'}
          </button>
        </div>
      )}

      {createdWarning && (
        <div className="error-box">
          {createdWarning.message} <Link to={`/projects/${createdWarning.id}`}>Åbn sagen</Link>
        </div>
      )}

      {showNewService && (
        <NewProjectForm
          initialKind="service"
          lockKind
          onDone={() => setShowNewService(false)}
          onCreated={(created, warning) => {
            setShowNewService(false)
            if (warning) setCreatedWarning({ id: created.project.id, message: warning })
            else navigate(`/projects/${created.project.id}`)
          }}
        />
      )}

      {scannedBox && (
        <CaseBoxChoice
          key={`${scannedBox.projectId}-${scannedBox.number}`}
          projectId={scannedBox.projectId}
          number={scannedBox.number}
          onScanNext={() => {
            setScannedBox(null)
            void startCamera()
          }}
        />
      )}

      <Card title="Scan mærkat">
        <p className="small muted" style={{ marginTop: 0 }}>
          Scan QR-koden på en sagskasse, en enhed eller et stykke værktøj. Mærkatet kan også scannes med
          mobilens eget kamera.
        </p>

        <div className="scanner" hidden={!scanning}>
          <video ref={videoRef} className="scanner__video" playsInline muted />
          <div className="scanner__frame" />
        </div>
        <canvas ref={canvasRef} hidden />

        <div className="btn-group" style={{ marginTop: scanning ? 12 : 0 }}>
          {scanning ? (
            <button className="btn" onClick={stop}>
              Stop kameraet
            </button>
          ) : (
            <button className="btn btn--primary" onClick={startCamera} disabled={busy}>
              Start kamera
            </button>
          )}
        </div>

        {busy && <Loading />}
        {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}
      </Card>

      <Card title="Eller tast koden">
        <p className="small muted" style={{ marginTop: 0 }}>
          De 8 tegn står under QR-koden på mærkatet.
        </p>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault()
            if (manualCode.trim()) void lookup(manualCode.trim())
          }}
        >
          <input
            style={{ maxWidth: 200, letterSpacing: 2, textTransform: 'uppercase' }}
            placeholder="K7M2QX9P"
            value={manualCode}
            onChange={(event) => setManualCode(event.target.value)}
          />
          <button className="btn btn--primary" type="submit" disabled={!manualCode.trim() || busy}>
            Slå op
          </button>
        </form>
      </Card>
    </div>
  )
}

/**
 * Landingssiden for QR-koden (`/e/:code`). Slår enheden op og sender brugeren
 * videre til den. Har man ikke adgang, sørger den almindelige login-omdirigering
 * for resten.
 */
export function DeviceCodeLandingPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void api
      .get<DeviceDetail>(`/api/devices/by-code/${normalizeDeviceCode(code ?? '')}`)
      .then((found) => {
        if (!cancelled) navigate(`/devices/${found.device.id}`, { replace: true })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Enheden kunne ikke slås op.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [code, navigate])

  if (error) {
    return (
      <Card title="Enheden blev ikke fundet">
        <ErrorBox error={new Error(error)} />
        <p className="small muted">
          Koden på mærkatet var <code>{code}</code>.
        </p>
      </Card>
    )
  }

  return <Loading />
}

/** Slukker kameraet og frigiver strømmen. */
function stopCamera(video: HTMLVideoElement | null) {
  const stream = video?.srcObject as MediaStream | null
  stream?.getTracks().forEach((track) => track.stop())
  if (video) video.srcObject = null
}
